import {
  IBaseQuery,
  ICheckUserLimitData,
  IInviteCodeSence,
  IProduct,
  IRepayDate,
  IRepayDateResp,
  IRepayDetail,
  ITermsAndInterestReq,
  IUserLimitInfo,
} from '@loan/vb-node-loan/types';
import api from '@loan/vb-node-loan/types/apiPlatform';
import { baseMixins } from '@common/mixins/baseMixins';
import { i18n } from '@tencent/vb-web-base';
import { isFusionBankApp, isWeChat, to } from '@tencent/vb-web-base-core';
import {
  ICommonRes,
  ICoupon,
  LimitState,
} from '@common/core/interface/IModel';
import { ProductInfoMap } from '@common/core/help/constant';
import { sortedByKey } from '@common/lib/helper';
import { sum } from '@common/lib/math';
import { NetworkError } from '@common/lib/error';
import { formatBillListDate } from '@common/lib/dayjs';
import { encryptTransfer } from '@tencent/vb-web-security';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { getUrlQuery } from '@common/utils/param';

dayjs.extend(isBetween);

let prodInfo: IProduct;
let prodInfoList: IProduct[] = [];
export interface ILimitInfo extends ICommonRes<IUserLimitInfo> {
  message: LimitState;
  userInfo?: {
    acctNo: string;
    uid: string;
    residentType: number;
  };
}

/**
 * 校验额度申请状态
 * @param productNo
 */
export async function checkLimitCreate(productNo: string) {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.user.requestPlatformUserCheckCreateLimit({ prodId: productNo })
  );
  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('额度创建状态查询异常'),
      tag: 'limitCreateError',
    });
  }
  const { code } = respData;
  if (code !== 0) {
    throw new NetworkError({
      code,
      message: respData.message,
      tag: 'limitCreateProblem',
      extInfo: respData.data.allowApplyTimeStr,
    });
  }
  return respData.data;
}

/**
 * 判断用户贷款状态
 * @param productNo 贷款产品编号
 */
export async function checkLoanStatus(productNo: string) {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.user.requestPlatformUserCheckStatus({ prodNo: productNo }),
  );

  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'checkLoanStatusError',
    });
  }
  const { code } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message: respData.message, tag: 'checkLoanStatusProplem' });
  }

  return respData.data;
}

/**
 * 获取当前产品编号
 * @returns
 */
export function getProductNo() {
  const defaultProdNo = isWeChat ? ProductInfoMap.DEFAULT : '';
  let currentProductNo: string;
  // 优先取url参数, 如果有值保存到内存变量，其他页面跳转时获取
  const prodNo = getUrlQuery('prodNo') || getUrlQuery('product_no') || getUrlQuery('productNo') || defaultProdNo;
  const proNoFromUrl = prodNo;
  if (isFusionBankApp) {
    return proNoFromUrl;
  }
  // 钱包贷款现在给出去的是没有带产品号的链接 所以这里只能默认写死ProductInfoMap.DEFAULT
  if (proNoFromUrl) {
    currentProductNo = proNoFromUrl;
  } else {
    // 从内存获取
    currentProductNo = ProductInfoMap.DEFAULT;
  }
  // console.log('currentProductNo', currentProductNo);
  // 最后如果产品id还是没取到，默认NPP

  return currentProductNo;
}

/**
 * 获取贷款产品信息
 */
export async function getProductInfo(cache = true) {
  const productNo = getProductNo();
  const { getT } = i18n;
  const $t = getT();
  if (cache && prodInfo && prodInfo.proNo === productNo) {
    return prodInfo;
  }

  const [rspData, err] = await to(
    api.loan.requestPlatformLoanGetProduct({
      prodNo: productNo,
    }),
  );
  if (err) {
    throw new NetworkError({ code: err?.status, message: $t('获取产品异常'), tag: 'productError' });
  }
  if (rspData.code !== 0) {
    throw new NetworkError({ code: rspData.code, message: rspData.message, tag: 'productProblem' });
  }

  if (rspData.data.proList && rspData.data.proList.length > 0 && rspData.data.proList[0].proNo === productNo) {
    const prod = rspData.data.proList[0];
    if (cache) {
      prodInfo = prod;
    }
    return prod;
  }
  throw new NetworkError({ code: 1040920001, message: $t('获取产品为空'), tag: 'productError' });
}

/**
 * 获取贷款产品信息
 */
export async function getProductInfoList(cache = true) {
  const { getT } = i18n;
  const $t = getT();
  if (cache && prodInfoList.length > 0) {
    return prodInfoList;
  }
  const [rspData, err] = await to(
    api.loan.requestPlatformLoanGetProduct({}),
  );
  if (err) {
    throw new NetworkError({ code: err?.status, message: $t('获取产品异常'), tag: 'productError' });
  }
  if (rspData.code !== 0) {
    throw new NetworkError({ code: rspData.code, message: rspData.message, tag: 'productProblem' });
  }

  if (!rspData.data.proList?.[0]) {
    // 错误码系统中设置的错误码
    throw new NetworkError({ code: 1040920001, message: $t('获取产品为空'), tag: 'productError' });
  }
  if (cache) {
    prodInfoList = rspData.data.proList;
  }

  return rspData.data.proList;
}

/**
 * 获取用户账号信息
 */
export async function getAccountList() {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.common.requestPlatformCommonGetAcct(),
  );
  if (err) {
    throw new NetworkError({ code: err?.status, message: $t('获取户口异常'), tag: 'accNoError' });
  }
  if (respData.code !== 0) {
    throw new NetworkError({ code: respData.code, message: respData.message, tag: 'productProblem' });
  }

  if (respData.code === 0 && respData.data.list.length === 0) {
    throw new NetworkError({ code: respData.code, message: $t('获取户口列表为空'), tag: 'accListEmpty' });
  }

  return respData.data.list;
}

/**
 * 获取用户信息
 */
export async function getUserAdr() {
  let req = {};
  let decrypt = async (v: string) => await v;
  if (!isFusionBankApp) {
    const crypt = await encryptTransfer();
    const { key, iv } = crypt;
    decrypt = crypt.decrypt;
    req = {
      key,
      iv,
    };
  }

  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.user.requestPlatformUserGetUserInfo(req),
  );
  if (err) {
    throw new NetworkError({ code: err?.status, message: $t('获取用户信息异常'), tag: 'userInfoError' });
  }
  if (respData.code !== 0) {
    throw new NetworkError({ code: respData.code, message: respData.message, tag: 'userInfoProblem' });
  }
  const residenceAddress = await decrypt(respData.data.residenceAddress);
  let addr1 = '';
  let addr2 = '';
  if (respData.data.addrArr[0]) {
    addr1 = await decrypt(respData.data.addrArr[0]);
  }
  if (respData.data.addrArr[1]) {
    addr2 = await decrypt(respData.data.addrArr[1]);
  }

  return {
    residenceAddress,
    addrArr: [addr1, addr2],
    name: respData.data.name,
    enName: respData.data.enName,
    mobile: respData.data.mobile,
    gsmCode: respData.data.gsmCode
  };
}

export async function getRepayRecent(req: IBaseQuery) {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.repay.requestPlatformRepayGetRepayRecent(req),
  );
  if (err) {
    throw new NetworkError({ code: err?.status, message: $t('最近待还异常'), tag: 'recentPayError' });
  }
  const { code, data, message } = respData;

  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'recentPayProblem' });
  }

  // 根据创建时间排序，原本有多条借据，目前只有一条
  // data.details = sortedByKey(data.details, 'createAt').reverse();

  return data;
}

export async function getRepayBook(req: {
  offset: number;
  limit: number;
  prodNo?: string;
  baseProdNo?: string;
  ccy?: string;
}) {
  const { getT } = i18n;
  const $t = getT();
  const proNo = req.prodNo || '';
  const baseProNo = req.baseProdNo || '';
  const ccyCode = req.ccy || '';
  const [respData, err] = await to(
    api.repay.requestPlatformRepayGetRepayBook({
      limit: req.limit.toString(),
      offset: req.offset.toString(),
      ccyCode,
      proNo,
      baseProNo,
    }),
  );
  if (err !== null) {
    throw new NetworkError({ code: err?.status, message: $t('获取还款流水失败'), tag: 'repayBookError' });
  }

  const { code, data, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'repayBookError' });
  }

  return data.repayBooks;
}

export async function getLoanTerms(
  amount: string,
  limitCode: string,
  proNo: string,
  seq: string,
) {
  const { getT } = i18n;
  const $t = getT();
  const params = {
    prodId: proNo,
    limitCode,
    principal: amount,
    seq,
  };
  const [respData, err] = await to(
    api.loan.requestPlatformLoanGetTerms(params),
  );

  if (err) {
    throw new NetworkError({ code: err?.status, message: $t('获取还款期失败'), tag: 'getTermsError' });
  }
  const { code, data, message } = respData;

  // 1613239250 年龄超过限制 会返回data内容
  // 1613239237 DSR不足 会返回data内容
  if (code !== 0 && code !== 1613239250 && code !== 1613239237) {
    throw new NetworkError({ code, message, tag: 'getTermsError' });
  }
  if (code === 1613239250) {
    data.maxTerm = -1;
  }

  return data;
}

export async function getBillList(offset: number, limit: number) {
  const { getT } = i18n;
  const $t = getT();
  const productInfo = await getProductInfo();
  const [loanBills, err] = await to(
    api.repay.requestPlatformRepayGetLoanBillList({
      baseProNo: productInfo.baseProNo,
      limit: limit.toString(),
      offset: offset.toString(),
    }),
  );
  if (err !== null) {
    throw new NetworkError({ code: err?.status, message: $t('贷款列表异常'), tag: 'billListError' });
  }
  const { data, message, code } = loanBills;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'billListProblem' });
  }
  const list = data.billList;
  // 根据创建时间倒序
  const res = sortedByKey(list, 'createAt').reverse();
  return res.map((i) => ({ ...i, createAt: formatBillListDate(i.createAt) }));
}

export async function getAllBillList(req: {
  offset: number;
  limit: number;
  baseProNo?: string;
  overdueInd?: string;
  loanState?: 'CLOSE' | 'NORMAL' | undefined;
}) {
  const { getT } = i18n;
  const $t = getT();
  const [loanBills, err] = await to(
    api.repay.requestPlatformRepayGetAllBillList({
      baseProNo: req.baseProNo,
      overdueInd: req.overdueInd,
      loanState: req.loanState,
      limit: req.limit.toString(),
      offset: req.offset.toString(),
    }),
  );
  if (err !== null) {
    throw new NetworkError({ code: err?.status, message: $t('贷款列表异常'), tag: 'billListError' });
  }
  const { data, message, code } = loanBills;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'billListProblem' });
  }
  const list = data.billList;
  // 根据创建时间倒序
  const res = sortedByKey(list, 'createAt').reverse();
  return res.map((i) => ({ ...i, createAt: formatBillListDate(i.createAt) }));
}

export async function getInitiativeDetail(
  id: string,
  finished: boolean,
  writeOff: boolean,
) {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.repay.requestPlatformRepayLoanInitiative({ loanNo: id, finished, writeOff }),
  );
  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('获取提前还款信息异常'),
      tag: 'initiativeDetailError',
    });
  }
  const { code, data, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'initiativeDetailProblem' });
  }

  return data;
}

interface ILoanDetail extends IRepayDetail {
  /** 当前期数 */
  nowPeriod: number;
  /** 总期数 */
  totPeriod: number;
  /** 逾期 */
  duePeriod: number;
  /** 应还金额 */
  shouldPay: string;
  /** 逾期费用 */
  overdueFee: string;
  /** 逾期本金 */
  totOverduePrcp: string;
  /** 逾期罚息 */
  totOverduePenalty: string;
  /** 逾期利息 */
  totOverdueInterest: string;
}

export async function getBillDetail(loanNo: string) {
  const { getT } = i18n;
  const $t = getT();

  const [loanDetail, err] = await to(
    api.repay.requestPlatformRepayGetLoanDetail({ loanNo }),
  );
  if (err !== null) {
    throw new NetworkError({ code: err?.status, message: $t('获取详情异常'), tag: 'detailError' });
  }
  const { data, code, message } = loanDetail;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'detailProblem' });
  }
  const res: ILoanDetail = {
    ...data,
    nowPeriod: 0,
    totPeriod: 0,
    duePeriod: 0,
    shouldPay: '0.00',
  };

  // 对还款流水进行排序，按照还款时间升序
  res.repayBooks = sortedByKey(data.repayBooks, 'repayDate');
  // 对还款计划进行排序，默认还款期升序
  res.schedules = sortedByKey(data.schedules, 'periodNo');

  if (data.close) {
    // 借据已到期
    res.nowPeriod = data.repayBooks.length + 1;
    res.totPeriod = data.repayBooks.length;
  } else {
    // 如果一笔核销借据在系统自动扣款的时候，只是还了核销本金 但是 核销利息或者核销借据中产生的逾期手续费（分期贷）还没还清，那么close就不可能为true，这时候res.schedules就会是空 因为里面就全部都是结清状态，但是逾期手续费或者利息还没扣
    // 借据未到期
    // 当前期为还款计划中的第一条计划
    const now = res.schedules[0];
    // 核销的借据是有可能本息都还完了 但是 剩下逾期手续费没还的，这种情况下 schedules就是空数组了
    if (res.writeOff && !now) {
      res.shouldPay = res.totOverdueMoney;
      res.nowPeriod = data.repayBooks.length + 1;
      res.totPeriod = data.repayBooks.length;
    } else {
      if (res.overdue) {
        // 借据已逾期
        // 起始逾期期数
        res.duePeriod = now.periodNo;
        res.totPeriod = now.totPeriod;
        res.nowPeriod = Math.min(...res.schedules.filter((i) => !i.overdue).map((i) => i.periodNo), now.totPeriod + 1);
        res.totOverdueInterest = res.totOverdueInterest;
        res.totOverduePrcp = res.totOverduePrcp;
        res.totOverduePenalty = res.totOverduePenalty;
        res.shouldPay = res.totOverdueMoney;
      } else {
        res.nowPeriod = now.periodNo;
        res.totPeriod = now.totPeriod;
        res.shouldPay = now.needToPayLeft;
      }
      if (res.writeOff) {
        res.shouldPay = res.totOverdueMoney;
      }
    }
  }
  return res;
}

/**
 * 检查提款状态
 * @param tradeNo 交易号
 */
export async function checkLoanDealStaus(tradeNo: string) {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.repay.requestPlatformRepayCheckLoanStatus({ tradeNo }),
  );
  if (err) {
    throw new NetworkError({ code: err?.status, message: $t('贷款列表异常'), tag: 'billListError' });
  }
  const { code, data, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'loanStatusProblem' });
  }

  return data;
}

// 检查还款状态
export async function checkRepayDealStaus(tradeNo: string) {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.repay.requestPlatformRepayCheckRepayStatus({ tradeNo }),
  );
  if (err) {
    throw new NetworkError({ code: err?.status, message: $t('贷款列表异常'), tag: 'billListError' });
  }
  const { code, data, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'loanStatusProblem' });
  }

  return data;
}

export async function getLoanLimitList() {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.loan.requestPlatformLoanGetUserLimitInfoList({}),
  );
  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getUserLimitInfoListError',
    });
  }
  const { code, message, data } = respData;
  if (code !== 0 && code !== 1038850155) {
    throw new NetworkError({ code, message, tag: 'getUserLimitInfoListProblem' });
  }
  return data;
}

// 根据商品ID判断额度状态
export async function getLimitInfo() {
  const { checkIsInstallment, getBaseParam } = baseMixins();
  const productNo = getBaseParam('prodNo');
  // getProductInfo有缓冲
  const { getT } = i18n;
  const $t = getT();
  const param: {
    productNo: string;
    status?: string;
    seq?: string;
  } = { productNo };
  const queryObj = getUrlQuery();
  if (queryObj.seq || queryObj.limit_seq_no) {
    param.seq = (queryObj.seq || queryObj.limit_seq_no) as string;
  }
  // if (!param.seq && product.productTag === ProductType.INSTALLMENT) {
  //   // 分期贷款里面当前有效的额度值
  //   const curValidStatus = [0, 14, 18, 5, 16];
  //   param.status = limitStatus && limitStatus.length > 0 ? limitStatus.join(',') : curValidStatus.join(',');
  // }
  const [respData, err] = await to(
    api.loan.requestPlatformLoanGetUserLimitInfoList(param),
  );
  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getUserLimitInfoListError',
    });
  }
  const { code, data, message } = respData;
  // 1038850155 没有香港证件号
  if (code !== 0 && code !== 1038850155) {
    throw new NetworkError({ code, message, tag: 'getUserLimitInfoListProblem' });
  }

  // 3. 该产品没额度，确认下有没有存在其他商品额
  const limitList: IUserLimitInfo[] = data.list.filter(
    (item: IUserLimitInfo) => item.product_no && item.product_no.toString() === productNo,
  );
  const pinfo: ILimitInfo = {
    message: LimitState.INIT,
    userInfo: data.userInfo,
  };
  if (limitList.length === 0) {
    return pinfo;
  }
  pinfo.data = limitList[0];
  let { status } = limitList[0];
  if (!param.seq && checkIsInstallment()) {
    // 额度列表会把15的状态拉出来 如果没有指定seq的话 那么 分期贷当中如果查到状态15的 这时候是上一笔分期贷已经借款还未结清，这时候新的直接当成9处理就好
    if (status === 15) {
      status = 9;
    }
  }
  switch (status) {
    case 5:
      pinfo.message = LimitState.NORMAL;
      break;
    case 16:
      pinfo.message = LimitState.BINDING;
      break;
    case 9:
      pinfo.message = LimitState.INIT;
      break;
    case 0:
    case 14: // 落入人工地址证明了，去到了审核中页面 去查询是否需要进入地址证明上传页面
    case 18: // 提额审批中
      pinfo.message = LimitState.CREATING; // 创建中
      break;
    case 15:
      pinfo.message = LimitState.USING; // 额度使用中
      break;
    case 7:
    case 13:
      pinfo.message = LimitState.REJECT; // 拒绝申请
      break;
    case 23:
      pinfo.message = LimitState.LIMIT_FREEZING; // LIMIT_FREEZING 非永居证件过期冻结
      break;
  }

  return pinfo;
}

// 根据商品ID判断额度状态
export async function getUserProductLimitStatus() {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.loan.requestPlatformLoanGetUserLimitInfoList({})
  );
  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getUserLimitInfoListError',
    });
  }
  const { code, data, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'getUserLimitInfoListProblem' });
  }
  return data;
}

// 查询tesla白名单
export async function getTeslaWhiteList() {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.home.requestPlatformHomeGetTeslaWhiteList()
  );
  if (err) {
    throw new NetworkError({ code: err?.status, message: $t('获取详情异常'), tag: 'detailError' });
  }
  const { code, data, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'detailError' });
  }
  return data;
}

export async function getProductConfigs(params: { productNo: string; source?: 'app' | 'wallet' }) {
  const { getT } = i18n;
  const $t = getT();
  const { productNo, source } = params;
  const [respData, err] = await to(
    api.product.requestPlatformProductGetConfigs({ productNo, source }),
  );

  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getProductConfigs',
    });
  }
  const { code, data, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'getProductConfigs' });
  }

  return data;
}

export async function getTeslaAcct(params: { prodNo: string; seq: string }) {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(api.tesla.requestPlatformTeslaGetAcct(params));

  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getTeslaConfigs',
    });
  }
  const { code, data } = respData;
  if (code !== 0) {
    return {
      product_id: '',
      inner_acct_no: '',
      enable_entrusted_payment: 0,
      enable_use_self_acct: 0,
      userAcct: '',
      acct_info: [],
    };
  }

  return {
    product_id: data?.product_id || '',
    inner_acct_no: data?.inner_acct_no || '',
    enable_entrusted_payment: data?.enable_entrusted_payment || 0,
    enable_use_self_acct: data?.enable_use_self_acct || 0,
    userAcct: data?.userAcct || '',
    acct_info: data?.acct_info || [],
  };
}

export async function getPayDealInfo(param: { tradeNo?: string; loanNo?: string }) {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.tesla.requestPlatformTeslaGetDealInfo(param),
  );

  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getTeslaConfigs',
    });
  }
  const { code, data, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'getTeslaConfigs' });
  }

  return data!;
}

/**
 * 用户地址校验
 * @param productNo 贷款产品编号
 */
export async function checkLimit(productNo: string, seq: string, willIncrease?: '1' | '0'): Promise<ICheckUserLimitData> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.user.requestPlatformUserCheckUserLimit({ prodId: productNo, seq, increaseLimitScene: willIncrease }),
  );
  if (err) {
    throw new NetworkError({ code: err?.status, message: $t('系统繁忙，请稍后重试'), tag: 'addressError' });
  }
  const { code, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'addressProblem' });
  }

  return respData.data;
}

/**
 * @param {IGetInterestQuery} req 查询参数
 * @returns {Promise<IInterestAndTermsRes>}
 */
export async function termsAndInterest(req: ITermsAndInterestReq){
  req.amount = sum([req.amount], 2);
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.loan.requestPlatformLoanTermsAndInterest(req),
  );
  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'termsAndInterestError',
    });
  }
  const { code, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'termsAndInterestProblem' });
  }

  return respData.data;
}

/**
 * 获取还款日信息
 *
 */
export async function getRepayDate(req: IRepayDate): Promise<IRepayDateResp> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.repay.requestPlatformRepayGetRepayDate(req),
  );
  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getRepayDateError',
    });
  }
  const { code, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'getRepayDateProblem' });
  }

  return respData.data;
}

export async function queryProofQueryDimProof() {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.proof.requestPlatformProofQueyDimProof()
  );
  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'queryProofQueryDimProofError',
    });
  }
  const { code, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'queryProofQueryDimProofProblem' });
  }

  return respData.data.uploadInfo;
}

/**
 * 检查该用户下所有产品是否有逾期借据，有逾期借据的话 不能让这个人贷款
 * @returns
 */
export async function creditCheck() {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.repay.requestPlatformRepayInternalCreditCheck()
  );
  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'creditCheckError',
    });
  }
  const { code, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'creditCheckProblem' });
  }

  return respData.data;
}

/**
 * 按产品查询优惠券列表
 * @returns
 */
export async function queryCouponList({
  prodNo,
  money,
  defaultLimit,
  tenor,
  type,
}: {
  prodNo: string;
  money?: string;
  defaultLimit?: string;
  tenor?: string;
  type?: string;
}) {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.act.requestPlatformActQueryCouponList({
      product_no: prodNo,
      loan_tenor: tenor,
      loan_money: money,
      default_limit: defaultLimit,
      sub_trade_type: type,
      coupon_id: ''
    }),
  );
  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'queryCouponListError',
    });
  }
  const { code, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'queryCouponListProblem' });
  }

  return respData.data?.list as ICoupon[] || [];
}

/**
 * 查询优惠券详情
 * @returns
 */
export async function queryCoupon(
  id: string,
  productNo: string,
  money?: string,
  tenor?: string,
  type?: string,
) {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.act.requestPlatformActQueryCoupon({
      coupon_id: id,
      product_no: productNo,
      loan_tenor: tenor,
      loan_money: money,
      sub_trade_type: type,
    }),
  );
  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'queryCouponListError',
    });
  }
  const { code, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'queryCouponListProblem' });
  }

  return respData.data.coupon;
}

/**
 * check 邀请码
 * @returns
 */
export async function checkInviteCode(inviteCode: string, prodNo = '', sence?: IInviteCodeSence, seq?: string) {
  const senceId = getUrlQuery('senceId') || '';
  if (inviteCode) {
    inviteCode = inviteCode.trim();
    inviteCode = inviteCode.toLocaleUpperCase();
  }
  sence = sence || 'loan';
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.act.requestPlatformActCheckInviteCode({ code: inviteCode, prodNo, sence, senceId, seq }),
  );
  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'queryCouponListError',
    });
  }
  const { code, data, message } = respData;
  return {
    code, // 产品id校验是是否通过
    data: {
      text: data.text ?? '',
      code: data.code ?? inviteCode,
      pass: data.pass,
      notPassMessageList: data.notPassMessageList,
    },
    message,
  };
}

// 查询用户是否处于冻结状态
export async function getQuickQuickTransferInfo(productNo: string) {
  const { getT } = i18n;
  const $t = getT();
  const [res, err] = await to(
    api.loan.requestPlatformLoanGetQuickTransferInfo({ productNo }),
  );
  if (err) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getQuickQuickTransferInfo',
    });
  }
  const { code, message, data } = res;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'getQuickQuickTransferInfo' });
  }
  return data;
}

// 查配置
let getSwitchConfPromise!: Promise<{ code: number; message: string; data: { confData: any } }> | null;
export async function getSwitchConf(type: 'TESLA' | 'NPP') {
  const { getT } = i18n;
  const $t = getT();
  if (!getSwitchConfPromise) {
    getSwitchConfPromise = api.home.requestPlatformHomeGetSwitchConf({ type });
  }
  const [resp, err] = await to(getSwitchConfPromise);
  if (err !== null) {
    getSwitchConfPromise = null;
    throw new NetworkError({ code: err.code, message: $t('系统繁忙，请稍后重试'), tag: 'getSwitchConfError' });
  }
  const { code, message, data } = resp;
  if (code !== 0) {
    getSwitchConfPromise = null;
    throw { code, message, userTag: 'getSwitchConfProblem' };
  }
  return data;
}



// 获取所有产品借据列表
export async function getAllProductsBills() {
  const { getT } = i18n;
  const $t = getT();
  const [bills, err] = await to(
    api.repay.requestPlatformRepayGetAllProductsBillList(),
  );
  if (err !== null) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getAllProductsBillsProblem',
    });
  }
  const { data, message, code } = bills;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'getAllProductsBillsProblem' });
  }
  return data;
}

// 获取邀请码活动信息
export async function getInviteInfo(prodNo: string, source: 'app' | 'wallet', seq: string) {
  const { getT } = i18n;
  const $t = getT();
  const [res, err] = await to(
    api.act.requestPlatformActGetInviteInfo({
      prodNo,
      source,
      seq,
    }),
  );
  if (err !== null) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getInviteInfoProblem',
    });
  }
  const { data, message, code } = res;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'getInviteInfoProblem' });
  }
  return data;
}

// 获取邀请码活动信息
export async function setInviteInfo(prodNo: string, seq: string, code: string) {
  const { getT } = i18n;
  const $t = getT();
  const [res, err] = await to(
    api.act.requestPlatformActSetInviteCode({
      prodNo,
      seq,
      code,
    }),
  );
  if (err !== null) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'setInviteInfoProblem',
    });
  }
  if (res.code !== 0) {
    throw new NetworkError({ code: res.code, message: res.message, tag: 'setInviteInfoProblem' });
  }
  return true;
}

// 获取营销白名单信息
export async function getWhiteListItem(keyword: string) {
  const { getT } = i18n;
  const $t = getT();
  const [res, err] = await to(
    api.user.requestPlatformUserGetWhiteListItem({
      keyword,
    }),
  );
  if (err !== null) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getWhiteListItemProblem',
    });
  }
  if (res.code !== 0) {
    throw new NetworkError({ code: res.code, message: res.message, tag: 'getWhiteListItemProblem' });
  }
  return res.data.flag;
}

// 获取贷款红点配置
export async function getRedpointConfig() {
  const { getT } = i18n;
  const $t = getT();
  const [res, err] = await to(
    api.loanConfig.requestPlatformLoanConfigQueryRedPointConfig()
  );
  if (err !== null) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getRedpointConfigProblem',
    });
  }
  const { code, message, data } = res;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'getRedpointConfigProblem' });
  }
  return data;
}

// 获取贷款业务配置
export async function GetLoanConfigList<T>(key: string, noFilterWhite ?:'1' | '2'): Promise<T> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.loanConfig.requestPlatformLoanConfigQueryConfigList({ key, noFilterWhite }),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'queryConfigListError',
    });
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'queryConfigListError' });
  }
  return data;
}


// 获取贷款业务配置
export async function loanConfigListReq<T>(key: string, noFilterWhite?: '1' | '2'): Promise<T> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.loanConfig.requestPlatformLoanConfigQueryConfigList({ key, noFilterWhite }),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'queryConfigListError',
    });
  }
  const { code, message} = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'queryConfigListError' });
  }
  return respData;
}

// 获取车主信息
export async function queryCarOwnerInfo(isTesla?: boolean) {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.carOwner.requestPlatformCarOwnerQueryCarOwnerInfo({ carOwnerType: isTesla ? 'tesla' : undefined }),
  );

  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'queryCarOwnerInfoError',
    });
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'queryCarOwnerInfo' });
  }
  return data;
}

// 获取分期贷分段月平息
export async function queryInstallmentRate(isInstallment: boolean) {
  const prodNo = getProductNo();
  // 非分期贷不查询
  if (!isInstallment) {
    return {
      items: [],
    };
  }
  const { getT } = i18n;
  const $t = getT();
  const [res, err] = await to(
    api.loanConfig.requestPlatformLoanConfigQueryInstallmentRate({ prodNo }),
  );
  if (err !== null) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'queryInstallmentRateProblem',
    });
  }
  const { data, message, code } = res;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'queryInstallmentRateProblem' });
  }
  return data;
}

// 检查分期贷款入口是否打开
export async function getRetainCoupon(req: { limitPlatform?:string,prodNo: string; seq: string; loanEvent: string; limit: string }) {
  const { getT } = i18n;
  const $t = getT();
  const [resp, err] = await to(
    api.act.requestPlatformActGetRetainCoupon(req),
  );
  console.log('resp', resp);
  if (err !== null) {
    throw new NetworkError({
      code: err?.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'queryInstallmentRateProblem',
    });
  }
  const { message, code, data } = resp;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'queryInstallmentRateProblem' });
  }
  return data;
}

// 取消提额
export async function cancelIncreaseLimit(prodId: string, seq: string) {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.loan.requestPlatformLoanCancelIncreaseLimit({ prodId, seq }),
  );

  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'cancelIncreaseLimitError',
    });
  }
  const { code, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'cancelIncreaseLimit' });
  }
  return code;
}

// 取消提额检查
export async function checkCancelIncreaseLimit(prodId: string, seq: string) {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.loan.requestPlatformLoanCheckCancelIncreaseLimit({ prodId, seq }),
  );

  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'checkCancelIncreaseLimitError',
    });
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'checkCancelIncreaseLimit' });
  }
  return data;
}

/**
 * 用户地址校验
 * @param productNo 贷款产品编号
 * @param seq 流水号
 * @param operType // 0: 核验地址；1：修改地址
 */
export async function checkEprcAddress(prodId: string, seq: string, operType: number) {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.loan.requestPlatformLoanCheckEprcAddress({ prodId, seq, operType }),
  );
  if (err) {
    throw new NetworkError({ code: err?.status, message: $t('系统繁忙，请稍后重试'), tag: 'checkEprcAddressError' });
  }
  const { code, message } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'checkEprcAddressProblem' });
  }

  return respData.code;
}

/**
 * 查询合同key配置信息
 * @param productNo 贷款产品编号
 * @param sceneId 场景id
 */
export async function getContractKeys(productNo: string, sceneId: string) {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.common.requestPlatformCommonGetContractKeys({ productNo, sceneId }),
  );
  if (err) {
    throw new NetworkError({ code: err?.status, message: $t('系统繁忙，请稍后重试'), tag: 'getContractKeysError' });
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'getContractKeysProblem' });
  }
  const previewArr = data?.contract_busi.find((item) => item.busi_type === 'Preview')?.contract_key;
  const preViewObj: { [name: string]: string } = {};
  if (previewArr) {
    previewArr.forEach((item) => {
      preViewObj[item.contract_name] = item.contract_key;
    });
  }

  return preViewObj;
}
